import { mapMessage, mapPinnedMessage, mapThread } from '../api/mappers';
import { mergeThreadWithExisting } from '../lib/mergeThreadWithExisting';
import type { ChatRuntime } from '../api/runtime';
import { mapSocketItemToListItem, mapSocketMessageToMessageDto, mapSocketPinnedToPinnedDto } from '../api/socketMappers';
import type { ChatAction } from '../model/reducer';
import type { ChatListFilter, ChatMessage, ChatThread } from '../model/types';
import {
  CHAT_SOCKET_EVENT,
  ChatSocketUpdateReason,
  isChatSocketEvent,
  type ChatSocketServerEnvelope,
} from './chatSocketTypes';

export type ChatSocketEventSideEffects = {
  pinned?: {
    chatId: number;
    messages: ChatMessage[] | null;
  };
};

export type ChatSocketEventResult = {
  actions: ChatAction[];
  sideEffects: ChatSocketEventSideEffects;
};

export type ChatSocketEventContext = {
  currentUserId: number;
  activeChatId: number | null;
  filter: ChatListFilter;
  runtime: ChatRuntime;
  existingThreads: ChatThread[];
};

function emptyResult(): ChatSocketEventResult {
  return { actions: [], sideEffects: {} };
}

function threadMatchesFilter(thread: ChatThread, filter: ChatListFilter): boolean {
  if (filter === 'archived') {
    return thread.isArchived;
  }

  if (thread.isArchived) {
    return false;
  }

  if (filter === 'all') {
    return true;
  }

  return thread.kind === filter;
}

function mapSocketThread(
  dto: Parameters<typeof mapSocketItemToListItem>[0],
  ctx: ChatSocketEventContext,
  existing?: ChatThread,
): ChatThread {
  const thread = mapThread(mapSocketItemToListItem(dto), ctx.runtime, ctx.currentUserId);

  if (!existing) {
    return thread;
  }

  return mergeThreadWithExisting(existing, {
    ...thread,
    isArchived: existing.isArchived,
    pinnedMessage: existing.pinnedMessage ?? thread.pinnedMessage,
  });
}

function applyArchiveReason(
  thread: ChatThread,
  reason: ChatSocketUpdateReason,
  existing?: ChatThread,
): ChatThread {
  if (reason === ChatSocketUpdateReason.Archived) {
    return { ...thread, isArchived: true };
  }

  if (reason === ChatSocketUpdateReason.Unarchived) {
    return { ...thread, isArchived: false };
  }

  return {
    ...thread,
    isArchived: existing?.isArchived ?? thread.isArchived,
  };
}

function upsertOrRemoveThread(thread: ChatThread, filter: ChatListFilter): ChatAction[] {
  if (!threadMatchesFilter(thread, filter)) {
    return [{ type: 'threads/remove', payload: thread.id }];
  }

  return [{ type: 'threads/upsert', payload: thread }];
}

export function applyChatSocketEvent(
  envelope: ChatSocketServerEnvelope,
  ctx: ChatSocketEventContext,
): ChatSocketEventResult {
  if (isChatSocketEvent(envelope, CHAT_SOCKET_EVENT.MessageNew)) {
    const { chatId, message } = envelope.payload;

    if (chatId !== ctx.activeChatId) {
      return emptyResult();
    }

    return {
      actions: [
        {
          type: 'messages/upsert',
          payload: {
            chatId,
            items: [
              mapMessage(
                mapSocketMessageToMessageDto(message),
                chatId,
                ctx.currentUserId,
                ctx.runtime,
              ),
            ],
          },
        },
      ],
      sideEffects: {},
    };
  }

  if (isChatSocketEvent(envelope, CHAT_SOCKET_EVENT.MessageRead)) {
    const { chatId, userId, messages } = envelope.payload;

    if (chatId !== ctx.activeChatId) {
      return emptyResult();
    }

    return {
      actions: [
        {
          type: 'messages/updateReadReceipts',
          payload: {
            chatId,
            readerUserId: userId,
            currentUserId: ctx.currentUserId,
            receipts: messages,
          },
        },
      ],
      sideEffects: {},
    };
  }

  if (isChatSocketEvent(envelope, CHAT_SOCKET_EVENT.MessageRemoved)) {
    const { chatId, messageId, pinnedCleared } = envelope.payload;
    const actions: ChatAction[] = [
      {
        type: 'messages/remove',
        payload: { chatId, messageId },
      },
    ];

    if (pinnedCleared) {
      actions.push({
        type: 'threads/setPinnedMessage',
        payload: { chatId, pinnedMessage: null },
      });
    }

    return {
      actions,
      sideEffects: pinnedCleared
        ? {
            pinned: {
              chatId,
              messages: null,
            },
          }
        : {},
    };
  }

  if (isChatSocketEvent(envelope, CHAT_SOCKET_EVENT.MessagePinnedChanged)) {
    const { chatId, pinnedMessage } = envelope.payload;
    const mappedPinned = pinnedMessage
      ? mapPinnedMessage(
          mapSocketPinnedToPinnedDto(pinnedMessage),
          chatId,
          ctx.currentUserId,
          ctx.runtime,
        )
      : null;

    return {
      actions: [
        {
          type: 'threads/setPinnedMessage',
          payload: { chatId, pinnedMessage: mappedPinned },
        },
      ],
      sideEffects: {
        pinned: {
          chatId,
          messages: mappedPinned ? [mappedPinned] : null,
        },
      },
    };
  }

  if (isChatSocketEvent(envelope, CHAT_SOCKET_EVENT.ChatUpdated)) {
    const { chat, reason } = envelope.payload;
    const existing = ctx.existingThreads.find((thread) => thread.id === chat.id);
    const thread = applyArchiveReason(mapSocketThread(chat, ctx, existing), reason, existing);

    return {
      actions: upsertOrRemoveThread(thread, ctx.filter),
      sideEffects: {},
    };
  }

  if (isChatSocketEvent(envelope, CHAT_SOCKET_EVENT.ChatNew)) {
    const thread = mapSocketThread(envelope.payload.chat, ctx);

    return {
      actions: upsertOrRemoveThread(thread, ctx.filter),
      sideEffects: {},
    };
  }

  if (isChatSocketEvent(envelope, CHAT_SOCKET_EVENT.ChatDeleted)) {
    return {
      actions: [{ type: 'threads/remove', payload: envelope.payload.chatId }],
      sideEffects: {},
    };
  }

  return emptyResult();
}
